import React from "react";
import { MemberCompetencyDetailItem } from "../types";

interface CompetencyRadarChartProps {
  competencies: MemberCompetencyDetailItem[];
  size?: number;
}

// Convert proficiency level string to a percentage score for benchmark
const proficiencyToScore = (level: string): number => {
  const norm = (level || "").toLowerCase();
  if (norm.includes("expert") || norm.includes("master")) return 95;
  if (norm.includes("advanced") || norm.includes("senior")) return 80;
  if (norm.includes("intermediate") || norm.includes("mid")) return 60;
  if (norm.includes("beginner") || norm.includes("novice") || norm.includes("junior")) return 35;
  return 50;
};

export const CompetencyRadarChart: React.FC<CompetencyRadarChartProps> = ({
  competencies,
  size = 320,
}) => {
  if (!competencies || competencies.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-6 text-center text-slate-400 dark:text-neutral-500 bg-slate-50 dark:bg-neutral-900/40 rounded-xl border border-dashed border-slate-200 dark:border-neutral-800">
        <p className="text-xs font-medium">No competency evaluations recorded yet.</p>
        <span className="text-[11px] text-slate-400 mt-0.5">Competency scores are updated as the employee completes module diagnostics.</span>
      </div>
    );
  }

  // If fewer than 3 skills, radar polygons degenerate into a line or point, so render a sleek bar comparison
  if (competencies.length < 3) {
    return (
      <div className="space-y-4 p-4 bg-slate-50/60 dark:bg-neutral-900/40 rounded-xl border border-slate-200 dark:border-neutral-800">
        <div className="flex items-center justify-between text-xs text-slate-500 dark:text-neutral-400 border-b border-slate-200 dark:border-neutral-800 pb-2">
          <span>Competency Area</span>
          <div className="flex items-center space-x-3 text-[11px]">
            <span className="flex items-center space-x-1">
              <span className="w-2.5 h-2.5 rounded-full bg-violet-500 inline-block" />
              <span>Current</span>
            </span>
            <span className="flex items-center space-x-1">
              <span className="w-2.5 h-2.5 rounded-full border border-dashed border-slate-400 inline-block" />
              <span>Target</span>
            </span>
          </div>
        </div>
        {competencies.map((comp) => {
          const currentScore = Math.max(5, Math.min(100, comp.diagnosed_score || proficiencyToScore(comp.current_proficiency)));
          const targetScore = proficiencyToScore(comp.target_proficiency);
          return (
            <div key={comp.id} className="space-y-1.5">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-800 dark:text-neutral-200">
                <span>{comp.skill_name}</span>
                <span className="text-[11px] text-slate-500 font-normal">
                  {comp.current_proficiency} ({currentScore}%) / Target: {comp.target_proficiency} ({targetScore}%)
                </span>
              </div>
              <div className="relative h-2.5 w-full bg-slate-200 dark:bg-neutral-800 rounded-full overflow-hidden">
                <div
                  className="absolute top-0 bottom-0 left-0 bg-violet-600 rounded-full transition-all duration-500"
                  style={{ width: `${currentScore}%` }}
                />
                <div
                  className="absolute top-0 bottom-0 border-r-2 border-dashed border-amber-500 z-10"
                  style={{ left: `${targetScore}%` }}
                  title={`Target: ${targetScore}%`}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Radar geometry calculations
  const center = size / 2;
  const radius = (size / 2) - 45; // margin for labels
  const totalAxes = competencies.length;
  const angleStep = (Math.PI * 2) / totalAxes;

  // Grid levels (20%, 40%, 60%, 80%, 100%)
  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];

  const getCoordinates = (index: number, valueRatio: number) => {
    // Start at top (-PI/2) and rotate clockwise
    const angle = (index * angleStep) - (Math.PI / 2);
    const x = center + radius * valueRatio * Math.cos(angle);
    const y = center + radius * valueRatio * Math.sin(angle);
    return { x, y, angle };
  };

  // Build target benchmark polygon points
  const targetPoints = competencies.map((comp, i) => {
    const ratio = Math.max(0.1, Math.min(1.0, proficiencyToScore(comp.target_proficiency) / 100));
    const { x, y } = getCoordinates(i, ratio);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  // Build actual verified score polygon points
  const actualPointsData = competencies.map((comp, i) => {
    const rawScore = comp.diagnosed_score > 0 ? comp.diagnosed_score : proficiencyToScore(comp.current_proficiency);
    const ratio = Math.max(0.05, Math.min(1.0, rawScore / 100));
    return {
      ...getCoordinates(i, ratio),
      score: rawScore,
      skill_name: comp.skill_name,
      proficiency: comp.current_proficiency,
      status: comp.status,
    };
  });

  const actualPoints = actualPointsData.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg
          width={size}
          height={size}
          className="overflow-visible select-none"
        >
          {/* Concentric grid rings */}
          {gridLevels.map((lvl, idx) => {
            const ringPoints = Array.from({ length: totalAxes }).map((_, i) => {
              const { x, y } = getCoordinates(i, lvl);
              return `${x.toFixed(1)},${y.toFixed(1)}`;
            }).join(" ");

            return (
              <polygon
                key={`grid-ring-${idx}`}
                points={ringPoints}
                className="fill-none stroke-slate-200 dark:stroke-neutral-800"
                strokeWidth={idx === gridLevels.length - 1 ? "1.5" : "1"}
                strokeDasharray={idx === gridLevels.length - 1 ? "" : "3,3"}
              />
            );
          })}

          {/* Spokes radiating from center */}
          {Array.from({ length: totalAxes }).map((_, i) => {
            const { x, y } = getCoordinates(i, 1.0);
            return (
              <line
                key={`spoke-${i}`}
                x1={center}
                y1={center}
                x2={x}
                y2={y}
                className="stroke-slate-200 dark:stroke-neutral-800"
                strokeWidth="1"
              />
            );
          })}

          {/* Target Benchmark Polygon */}
          <polygon
            points={targetPoints}
            className="fill-amber-400/10 stroke-amber-500/70"
            strokeWidth="2"
            strokeDasharray="4,4"
          />

          {/* Actual Verified Skill Polygon */}
          <polygon
            points={actualPoints}
            className="fill-violet-600/25 stroke-violet-600"
            strokeWidth="2.5"
          />

          {/* Actual Data Points & Markers */}
          {actualPointsData.map((pt, i) => (
            <g key={`marker-${i}`}>
              <circle
                cx={pt.x}
                cy={pt.y}
                r="4.5"
                className="fill-white stroke-violet-600 stroke-[2.5px] shadow-sm cursor-pointer hover:r-6 transition-all"
              />
            </g>
          ))}

          {/* Vertex Skill Labels */}
          {competencies.map((comp, i) => {
            const { x, y } = getCoordinates(i, 1.18);
            const isLeft = x < center - 10;
            const isRight = x > center + 10;
            const anchor = isLeft ? "end" : isRight ? "start" : "middle";

            return (
              <text
                key={`label-${i}`}
                x={x}
                y={y}
                textAnchor={anchor}
                dominantBaseline="central"
                className="text-[10px] font-semibold fill-slate-700 dark:fill-neutral-300"
              >
                {comp.skill_name.length > 14 ? `${comp.skill_name.slice(0, 13)}…` : comp.skill_name}
              </text>
            );
          })}
        </svg>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center space-x-6 mt-2 pt-2 border-t border-slate-100 dark:border-neutral-800/80 w-full text-xs">
        <div className="flex items-center space-x-1.5">
          <span className="w-3 h-3 rounded-full bg-violet-600 shadow-sm" />
          <span className="font-medium text-slate-700 dark:text-neutral-300">Verified Proficiency</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <span className="w-3 h-0.5 border-b-2 border-dashed border-amber-500" />
          <span className="font-medium text-slate-700 dark:text-neutral-300">Target Benchmark</span>
        </div>
      </div>
    </div>
  );
};
